import { useNavigate } from 'react-router-dom';
import { ProtectedEmail, ProtectedPhone } from '@/components/feature/ProtectedContact';
import { CONTACT, whatsappHref } from '@/lib/contactProtect';

interface FooterProps {
  lang?: 'en' | 'tr';
}

const LABELS = {
  en: {
    tagline: 'Transforming visions into immersive experiences with over three decades of expertise in global event management and technical production.',
    contact: 'Contact',
    contactSub: 'Get in touch with our offices',
    istanbul: 'Istanbul Office',
    budapest: 'Budapest Office',
    copyright: `© 2002-${new Date().getFullYear()} CSG The Event Company. All rights reserved.`,
    services: 'Our Services',
    serviceList: [
      { label: 'Event Strategy & Planning', slug: 'corporate-events-conferences' },
      { label: 'Technical Production & AV', slug: 'technical-production-av' },
      { label: 'Venue Sourcing & Logistics', slug: 'stage-design-lighting' },
      { label: 'Live Event Execution', slug: 'award-ceremonies-galas' },
      { label: 'DMC / Destination Services', slug: 'dmc-destination-services' },
      { label: 'Hybrid & Virtual Events', slug: 'hybrid-virtual-events' },
    ],
    quickLinks: 'Quick Links',
    home: 'Home',
    about: 'About',
    portfolio: 'Portfolio',
    getQuote: 'Get a Quote',
  },
  tr: {
    tagline: '35 yılı aşkın küresel etkinlik yönetimi ve teknik prodüksiyon deneyimiyle vizyonları unutulmaz deneyimlere dönüştürüyoruz.',
    contact: 'İletişim',
    contactSub: 'Ofislerimizle iletişime geçin',
    istanbul: 'İstanbul Ofisi',
    budapest: 'Budapeşte Ofisi',
    copyright: `© 2002-${new Date().getFullYear()} CSG The Event Company. Tüm hakları saklıdır.`,
    services: 'Hizmetlerimiz',
    serviceList: [
      { label: 'Etkinlik Stratejisi & Planlama', slug: 'corporate-events-conferences' },
      { label: 'Teknik Prodüksiyon & AV', slug: 'technical-production-av' },
      { label: 'Mekan Seçimi & Lojistik', slug: 'stage-design-lighting' },
      { label: 'Canlı Etkinlik Yönetimi', slug: 'award-ceremonies-galas' },
      { label: 'DMC / Destinasyon Hizmetleri', slug: 'dmc-destination-services' },
      { label: 'Hibrit & Sanal Etkinlikler', slug: 'hybrid-virtual-events' },
    ],
    quickLinks: 'Hızlı Bağlantılar',
    home: 'Ana Sayfa',
    about: 'Hakkımızda',
    portfolio: 'Portföy',
    getQuote: 'Teklif Al',
  },
};

export default function Footer({ lang = 'en' }: FooterProps) {
  const t = LABELS[lang];
  const navigate = useNavigate();

  const handleSocialClick = (platform: string) => {
    try {
      if (typeof window !== 'undefined' && (window as Window & { gtag?: (...args: unknown[]) => void }).gtag) {
        (window as Window & { gtag?: (...args: unknown[]) => void }).gtag!('event', 'social_media_click', {
          event_category: 'social',
          event_label: platform,
          value: 1,
        });
      }
    } catch (error) {
      console.log('Analytics tracking error:', error);
    }
  };

  const homePath = lang === 'tr' ? '/tr' : '/';

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(homePath);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 350);
    }
  };

  const goToService = (slug: string) => {
    const path = lang === 'tr' ? `/tr/hizmetler/${slug}` : `/services/${slug}`;
    navigate(path);
  };

  return (
    <footer className="bg-gray-950 border-t border-gray-800">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-5">
              <img
                src="https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/c025c61ab5f461ba606f2e0df7119804.jfif"
                alt="CSG The Event Company Logo"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">{t.tagline}</p>
            <div className="flex space-x-3">
              <a
                href="https://www.linkedin.com/in/can-%C3%B6zen-8b15667/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-yellow-400 text-gray-400 hover:text-black rounded-lg transition-all duration-300 cursor-pointer"
                onClick={() => handleSocialClick('linkedin')}
              >
                <i className="ri-linkedin-fill text-base"></i>
              </a>
              <a
                href="https://www.instagram.com/csgtheeventcompany/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-yellow-400 text-gray-400 hover:text-black rounded-lg transition-all duration-300 cursor-pointer"
                onClick={() => handleSocialClick('instagram')}
              >
                <i className="ri-instagram-fill text-base"></i>
              </a>
              <a
                href={whatsappHref()}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-gray-800 hover:bg-yellow-400 text-gray-400 hover:text-black rounded-lg transition-all duration-300 cursor-pointer"
                onClick={() => handleSocialClick('whatsapp_footer')}
              >
                <i className="ri-whatsapp-fill text-base"></i>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t.services}</h4>
            <ul className="space-y-2.5">
              {t.serviceList.map((s) => (
                <li key={s.slug}>
                  <button
                    onClick={() => goToService(s.slug)}
                    className="text-gray-400 hover:text-yellow-400 text-sm transition-colors cursor-pointer text-left"
                  >
                    <i className="ri-arrow-right-s-line mr-1 text-yellow-400/60"></i>
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t.quickLinks}</h4>
            <ul className="space-y-2.5">
              {[
                { label: t.home, id: 'home' },
                { label: t.about, id: 'about' },
                { label: t.portfolio, id: 'portfolio' },
                { label: t.contact, id: 'contact' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollTo(link.id)}
                    className="text-gray-400 hover:text-yellow-400 text-sm transition-colors cursor-pointer text-left"
                  >
                    <i className="ri-arrow-right-s-line mr-1 text-yellow-400/60"></i>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t.contact}</h4>
            <div className="space-y-5">
              {/* Istanbul */}
              <div>
                <p className="text-yellow-400 text-xs font-semibold uppercase tracking-wide mb-2">{t.istanbul}</p>
                <div className="space-y-1.5">
                  <ProtectedEmail encoded={CONTACT.istanbulEmail} className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 text-sm transition-colors">
                    <i className="ri-mail-line text-yellow-400/70 text-xs"></i>
                  </ProtectedEmail>
                  <ProtectedPhone encoded={CONTACT.istanbulPhone} encodedRaw={CONTACT.istanbulPhoneRaw} className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 text-sm transition-colors">
                    <i className="ri-phone-line text-yellow-400/70 text-xs"></i>
                  </ProtectedPhone>
                </div>
              </div>
              {/* Budapest */}
              <div>
                <p className="text-yellow-400 text-xs font-semibold uppercase tracking-wide mb-2">{t.budapest}</p>
                <div className="space-y-1.5">
                  <ProtectedEmail encoded={CONTACT.budapestEmail} className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 text-sm transition-colors">
                    <i className="ri-mail-line text-yellow-400/70 text-xs"></i>
                  </ProtectedEmail>
                  <ProtectedPhone encoded={CONTACT.budapestPhone} encodedRaw={CONTACT.budapestPhoneRaw} className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 text-sm transition-colors">
                    <i className="ri-phone-line text-yellow-400/70 text-xs"></i>
                  </ProtectedPhone>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm text-center sm:text-left">{t.copyright}</p>
          <div className="flex items-center gap-1 text-gray-600 text-xs">
            <i className="ri-map-pin-line text-yellow-400/50"></i>
            <span>Istanbul · Budapest · Dubai</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
